import type { Palette } from '@mui/material/styles';
import {
    computeComponentColors,
    type ComponentColorResult,
} from '../../hooks/useThemeContrastColor';

// ─────────────────────────────────────────────────────────────────────────────
// Palette keys — declared first so derived types can reference them
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Colors always available in the base flexy-components theme.
 */
export const CORE_PALETTE_KEYS = [
    'primary',
    'secondary',
    'warning',
    'success',
    'error',
    'info',
    'danger',
    'cardboard',
] as const;

/**
 * Colors added by app-level themes (e.g. l2d-modules).
 * Not present at BuildRawTheme time — injected in a second pass by the app.
 */
export const EXTENDED_PALETTE_KEYS = [
    'purple',
    'amber',
    'blueGrey',
    'deepOrange',
    'teal',
    'red',
    'pink',
    'lightGreen',
    'cyan',
    'green',
    'indigo',
    'lightBlue',
    'lime',
    'orange',
    'yellow',
    'blue',
    'brown',
    'deepPurple',
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type CorePaletteKey = (typeof CORE_PALETTE_KEYS)[number];
export type ExtendedPaletteKey = (typeof EXTENDED_PALETTE_KEYS)[number];
export type PaletteKey = CorePaletteKey | ExtendedPaletteKey;

/**
 * Type universel pour tous les tokens de composants.
 *
 * Chaque token est une map `clé → ComponentColorResult`.
 * Les clés sont libres : couleurs de palette (`PaletteKey`) pour chip/icon/button,
 * noms sémantiques (`'formalist'`, `'primary'`…) pour status/actionButton.
 *
 * Tous les tokens de composants partagent ce même type — ajouter une catégorie
 * revient simplement à l'ajouter dans `ComponentTokensShape`.
 */
export type ComponentTokenColorMap = Partial<Record<string, ComponentColorResult>>;

export type ComponentTokensShape = {
    /** Colors keyed by palette color name — chips, badges */
    chip: ComponentTokenColorMap;
    /** Colors keyed by palette color name — icon containers */
    icon: ComponentTokenColorMap;
    /** Colors keyed by palette color name — generic colored elements */
    button: ComponentTokenColorMap;
    /** Colors keyed by CTA variant name: primary / secondary / danger / warning */
    actionButton: ComponentTokenColorMap;
    /** Colors keyed by workflow status name: formalist / validator / client… */
    status: ComponentTokenColorMap;
};

// ─────────────────────────────────────────────────────────────────────────────
// Fallback (used when a key has no matching color in the map yet)
// ─────────────────────────────────────────────────────────────────────────────

const FALLBACK_TOKEN: ComponentColorResult = {
    backgroundColor: '#f5f5f5',
    backgroundColorOnHover: '#eeeeee',
    backgroundGradient: 'linear-gradient(135deg, #f5f5f5, #eeeeee)',
    backgroundGradientOnHover: 'linear-gradient(135deg, #eeeeee, #e0e0e0)',
    glassyStyle: { background: 'linear-gradient(135deg, #f5f5f5, #eeeeee)' },
    glassyStyleOnHover: { background: 'linear-gradient(135deg, #eeeeee, #e0e0e0)' },
    textColor: '#424242',
    textColorOnHover: '#212121',
};

// ─────────────────────────────────────────────────────────────────────────────
// MUI augmentation
// ─────────────────────────────────────────────────────────────────────────────

declare module '@mui/material/styles' {
    interface Palette {
        chip: ComponentTokenColorMap;
        icon: ComponentTokenColorMap;
        button: ComponentTokenColorMap;
        actionButton: ComponentTokenColorMap;
        status: ComponentTokenColorMap;
    }
    interface PaletteOptions {
        chip?: ComponentTokenColorMap;
        icon?: ComponentTokenColorMap;
        button?: ComponentTokenColorMap;
        actionButton?: ComponentTokenColorMap;
        status?: ComponentTokenColorMap;
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Builder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds a `ComponentTokensShape` from a resolved MUI palette.
 *
 * @param palette - A fully-resolved MUI `Palette` (output of `createTheme`)
 * @param keys    - Palette key names to include in chip/icon/button; defaults to `CORE_PALETTE_KEYS`
 */
export const buildComponentTokens = (
    palette: Palette,
    keys: readonly PaletteKey[] = CORE_PALETTE_KEYS,
): ComponentTokensShape => {
    const { mode } = palette;

    // ── chip / icon / button ─────────────────────────────────────────────────
    const colorMap: ComponentTokenColorMap = {};

    keys.forEach((key) => {
        const entry = (palette as unknown as Record<string, unknown>)[key];
        if (entry && typeof entry === 'object' && 'main' in (entry as object)) {
            colorMap[key] = computeComponentColors((entry as { main: string }).main, mode);
        }
    });

    const fromMap = (key: PaletteKey): ComponentColorResult => colorMap[key] ?? FALLBACK_TOKEN;

    // ── status ───────────────────────────────────────────────────────────────
    const status: ComponentTokenColorMap = {
        formalist: fromMap('primary'),
        formalistAction: fromMap('deepOrange'),
        validator: fromMap('purple'),
        client: fromMap('primary'),
        clientAction: fromMap('amber'),
        success: fromMap('success'),
        danger: fromMap('error'),
        inactive: fromMap('blueGrey'),
    };

    // ── actionButton ─────────────────────────────────────────────────────────
    const makeToken = (
        bg: string,
        bgHover: string,
        text: string,
        textHover: string,
    ): ComponentColorResult => ({
        backgroundColor: bg,
        backgroundColorOnHover: bgHover,
        backgroundGradient: bg,
        backgroundGradientOnHover: bgHover,
        glassyStyle: { background: bg },
        glassyStyleOnHover: { background: bgHover },
        textColor: text,
        textColorOnHover: textHover,
    });

    const actionButton: ComponentTokenColorMap = {
        primary: makeToken(
            palette.primary.main,
            palette.primary.light,
            palette.background.paper,
            palette.background.paper,
        ),
        secondary: makeToken(
            palette.grey[200],
            palette.grey[300],
            palette.text.primary,
            palette.text.primary,
        ),
        error: makeToken(
            palette.error.main,
            palette.error.light,
            palette.getContrastText(palette.error.main),
            palette.getContrastText(palette.error.light),
        ),
        warning: makeToken(
            palette.warning.light,
            palette.warning.main,
            palette.getContrastText(palette.warning.light),
            palette.getContrastText(palette.warning.main),
        ),
    };

    return { chip: colorMap, icon: colorMap, button: colorMap, actionButton, status };
};
